import {usePersonalMemos,usePersonalMemoSummary,useAdminSharedMemoSummary,memoTargetForChat} from '../../personalMemos/PersonalMemoProvider';
import ChatHeaderOverflowMenu from '../shared/ChatHeaderOverflowMenu';
import '../../../../css/common/PersonalMemos.css';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import ChatTopicTargetExpansion from '../topics/ChatTopicTargetExpansion';
import '../../../../css/common/chat/ChatTargetListRow.css';

const DEFAULT_CLASS_NAMES = {
  root: 'artist-list-item',
  left: 'artist-item-left',
  center: 'artist-item-center',
  nameLine: 'artist-name-line',
  name: 'artist-name',
  preview: 'artist-last-preview',
  below: 'artist-item-below',
  right: 'artist-item-right',
  time: 'artist-time',
  badge: 'new-message-badge',
};

const joinClassNames = (...tokens) => tokens.filter(Boolean).join(' ');

const ChatTargetListRow = ({
  className = '',
  classNames = {},
  onClick,
  onContextMenu,
  contextMenuItems = /** @type {Array<{key:string,label:React.ReactNode,icon?:string,onClick?:()=>void}>|undefined} */ (undefined),
  onTouchStart,
  onTouchEnd,
  onTouchCancel,
  onTouchMove,
  avatar = null,
  left = null,
  center = null,
  right = null,
  title = '',
  titleClassName = '',
  titleExtra = null,
  preview = null,
  previewNode,
  belowContent = null,
  topicScope,
  topicConversationId,
  hasTopics = false,
  onTopicSelect,
  time = null,
  badge = null,
  badgeClassName = '',
  ariaCurrent,
  ariaHidden,
}) => {
  const memoTarget=memoTargetForChat(topicScope,topicConversationId||'');
  const memos=usePersonalMemos();
  const memoSummary=usePersonalMemoSummary(memoTarget);
  const sharedSummary=useAdminSharedMemoSummary(memoTarget);
  const rowRef=useRef(null);
  const pressTimer=useRef(null);
  const ignoreClick=useRef(false);
  const [menuPoint,setMenuPoint]=useState(null);
  const ownsMenu=Boolean(memos&&memoTarget&&(!onContextMenu || contextMenuItems));
  const clearPress=useCallback(()=>{if(pressTimer.current)clearTimeout(pressTimer.current);pressTimer.current=null;},[]);
  const closeMenu=useCallback(()=>{setMenuPoint(null);ignoreClick.current=false;},[]);
  useEffect(()=>clearPress,[clearPress]);
  const interactive=(event)=>event.target?.closest?.('button,a,input,textarea,select,[role="menuitem"]');
  const hasSelectedRowText=()=>{const selection=window.getSelection();return Boolean(selection?.toString()&&rowRef.current?.contains(selection.anchorNode));};
  const contextMenu=(event)=>{
    if(!ownsMenu){onContextMenu?.(event);return;}
    if(interactive(event))return;
    clearPress();if(hasSelectedRowText())return;
    event.preventDefault();event.stopPropagation();
    setMenuPoint({x:event.clientX,y:event.clientY});
  };
  const touchStart=(event)=>{
    if(!ownsMenu){onTouchStart?.(event);return;}
    clearPress();ignoreClick.current=false;
    if(interactive(event)||event.touches.length!==1)return;
    const {clientX:x,clientY:y}=event.touches[0];
    pressTimer.current=setTimeout(()=>{pressTimer.current=null;if(hasSelectedRowText())return;ignoreClick.current=true;setMenuPoint({x,y});},650);
  };
  const keyboardMenu=(event)=>{
    if(event.target!==rowRef.current||!ownsMenu||!(event.key==='ContextMenu'||(event.shiftKey&&event.key==='F10')))return;
    event.preventDefault();const rect=rowRef.current.getBoundingClientRect();setMenuPoint({x:rect.left,y:rect.bottom});
  };
  const merged = { ...DEFAULT_CLASS_NAMES, ...(classNames || {}) };
  const hasMemo = Boolean(memos && memoTarget && (memoSummary?.hasMemo || sharedSummary?.hasMemo));
  const hasBelowContent = Boolean(belowContent);
  const hasTopicContent = Boolean(hasTopics && topicConversationId);
  const leftNode = left || (
    <div className={merged.left}>
      {avatar}
    </div>
  );
  const centerNode = center || (
      <div className={merged.center}>
        <div className={merged.nameLine || undefined}>
          <span className={joinClassNames(merged.name, titleClassName)}>{title}</span>
          {titleExtra}
        </div>
      {previewNode !== undefined ? previewNode : (preview != null ? <div className={merged.preview}>{preview}</div> : null)}
    </div>
  );
  const rightNode = right || (
    <div className={merged.right}>
      {time != null ? <span className={merged.time}>{time}</span> : null}
      {badge != null ? <span className={joinClassNames(merged.badge, badgeClassName)}>{badge}</span> : null}
    </div>
  );

  return (
    <li
      className={joinClassNames(merged.root, 'chat-target-list-row', hasMemo ? 'chat-target-list-row--has-memo' : '', hasBelowContent ? 'chat-target-list-row--has-below' : '', hasTopicContent ? 'chat-target-list-row--has-topics' : '', className)}
      ref={rowRef}
      tabIndex={ownsMenu?0:undefined}
      aria-keyshortcuts={ownsMenu?'Shift+F10':undefined}
      onKeyDown={keyboardMenu}
      onClick={event=>{if(ignoreClick.current){ignoreClick.current=false;event.preventDefault();return;}onClick?.(event);}}
      onContextMenu={contextMenu}
      onTouchStart={touchStart}
      onTouchEnd={ownsMenu?clearPress:onTouchEnd}
      onTouchCancel={ownsMenu?clearPress:onTouchCancel}
      onTouchMove={ownsMenu?clearPress:onTouchMove}
      aria-current={ariaCurrent}
      aria-hidden={ariaHidden}
    >
      {ownsMenu&&<ChatHeaderOverflowMenu ariaLabel="チャットの操作" items={contextMenuItems||[]} memoTarget={memoTarget} memoName={typeof title==='string'?title:''} contextMenuPosition={menuPoint} onContextMenuClose={closeMenu} contextAnchorRef={rowRef}/>}
      {leftNode}
      {centerNode}
      {rightNode}
      {hasBelowContent ? (
        <div className={joinClassNames(merged.below, 'chat-target-list-row__below')}>{belowContent}</div>
      ) : null}
      {hasMemo&&<div className="personal-memo-preview-stack">
      {sharedSummary?.hasMemo&&<button type="button" className="personal-memo-preview personal-memo-preview--shared" onClick={event=>{event.stopPropagation();memos.open(memoTarget,typeof title==='string'?title:'','admin_shared');}} onContextMenu={event=>event.stopPropagation()} onTouchStart={event=>event.stopPropagation()} aria-label={`${typeof title==='string'?title:''}の共有メモを開く`}><span className="personal-memo-preview__paper"><span className="personal-memo-preview__text">{sharedSummary.preview}</span></span></button>}
      {memoSummary?.hasMemo&&<button type="button" className="personal-memo-preview" onClick={event=>{event.stopPropagation();memos.open(memoTarget,typeof title==='string'?title:'');}} onContextMenu={event=>event.stopPropagation()} onTouchStart={event=>event.stopPropagation()} aria-label={`${typeof title==='string'?title:''}のメモを開く`}><span className="personal-memo-preview__paper"><span className="personal-memo-preview__text">{memoSummary.preview}</span></span></button>}
      </div>}
      {hasTopicContent ? <ChatTopicTargetExpansion scope={topicScope} conversationId={topicConversationId} hasTopics={hasTopics} onSelect={onTopicSelect} /> : null}
    </li>
  );
};

export default ChatTargetListRow;
